import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLogin } from '../hooks/useLogin';
import Swal from 'sweetalert2';
import Logo from '../images/logo_white.png';
import electronBridge from '../utils/electronBridge';

export default function ApiKeyLogin() {
    const {
        apiKeyInput,
        setApiKeyInput,
        apiKeyEmailInput,
        setApiKeyEmailInput,
        apiKeyLoading,
        apiKeyError,
        apiKeyLogin,
        accountContext
    } = useLogin();

    const navigate = useNavigate();

    // Redirect to profile if already logged in
    React.useEffect(() => {
        if (accountContext.loggedIn) {
            navigate('/profile', { replace: true });
        }
    }, [accountContext.loggedIn, navigate]);

    const openPManager = () => {
        const email = window.localStorage.getItem('apiKeyEmail') || '';
        const apiKey = window.localStorage.getItem('apiKey') || '';
        if (electronBridge.isElectron) {
            const { ipcRenderer } = electronBridge;
            ipcRenderer.send('open-pmanager-apikey', email, apiKey);
        } else {
            window.location.href = `promptus-pm:/email=${email},apiKey=${apiKey}`;
        }
    };

    const handleApiKeyLogin = async () => {
        await apiKeyLogin(() => {
            Swal.fire({
                icon: 'success',
                title: 'Welcome to Promptus!',
                html: `
                    <p style="margin-bottom: 12px;">You're all set! Your API key has been verified and you're signed in.</p>
                    <p style="color: #999; font-size: 0.9em;">To start generating with Bytedance models, launch <strong>PManager</strong> and start the <strong>Bytedance local server</strong>. It only takes a moment!</p>
                `,
                showCancelButton: true,
                confirmButtonText: 'Open PManager',
                cancelButtonText: 'Later',
                confirmButtonColor: '#FFB02E',
                cancelButtonColor: '#6B7280'
            }).then((result) => {
                if (result.isConfirmed) {
                    openPManager();
                }
            });
        });
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-full p-4">
            <div className="w-full max-w-md bg-dark-blue/60 rounded-2xl p-8">
                <div className="flex flex-col items-center mb-8">
                    <img src={Logo} alt="Promptus" className="w-16 h-16 mb-4" />
                    <h1 className="text-2xl font-bold text-white">API Key Login</h1>
                    <p className="text-sm text-yellow mt-2">Sign in with your API key</p>
                </div>

                <div className="flex flex-col gap-4">
                    <div>
                        <label className="block mb-2 text-sm font-medium text-white" htmlFor="apiKeyEmailInput">
                            Email Address
                        </label>
                        <input
                            type="email"
                            id="apiKeyEmailInput"
                            name="apiKeyEmailInput"
                            value={apiKeyEmailInput}
                            onChange={(e) => setApiKeyEmailInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleApiKeyLogin(); }}
                            className="w-full rounded-lg text-dark-blue border-0 p-3"
                            placeholder="Email Address"
                        />
                    </div>

                    <div>
                        <label className="block mb-2 text-sm font-medium text-white" htmlFor="apiKeyInput">
                            API Key
                        </label>
                        <input
                            type="password"
                            id="apiKeyInput"
                            name="apiKeyInput"
                            value={apiKeyInput}
                            onChange={(e) => setApiKeyInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleApiKeyLogin(); }}
                            className="w-full rounded-lg text-dark-blue border-0 p-3"
                            placeholder="API Key"
                        />
                    </div>

                    {apiKeyError && (
                        <div className="p-2 bg-red-100 border border-red-400 text-red-700 text-center font-semibold rounded">
                            {apiKeyError}
                        </div>
                    )}

                    <button
                        onClick={handleApiKeyLogin}
                        className="w-full rounded-lg mt-2 p-3 bg-yellow text-dark-blue shadow font-bold disabled:opacity-50"
                        disabled={apiKeyLoading || !apiKeyInput.trim() || !apiKeyEmailInput.trim()}
                    >
                        {apiKeyLoading ? (
                            <div className="flex items-center justify-center">
                                <div className="w-5 h-5 border-2 border-dark-blue/20 border-t-dark-blue rounded-full animate-spin mr-2" />
                                Verifying...
                            </div>
                        ) : (
                            'Verify & Login'
                        )}
                    </button>

                    <button
                        onClick={() => navigate('/profile')}
                        className="text-sm text-yellow/60 hover:text-yellow mt-2 text-center"
                    >
                        Sign in with email instead
                    </button>
                </div>
            </div>
        </div>
    );
}
